import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MainLayout } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import { useTranslation } from "react-i18next";
import { Base } from "../../framework/common/base";
import { getOpenTransportOrders, getOwnTransportOrders } from "../../services/transportOrderService";
import { useSelectCurrentWorkShiftTimeSlot } from "../../hooks/worktime/useSelectWorkShiftTimeSlots";
import { TransportOrderListItemDto, TransportOrderState } from "../../models/transport/transportOrder";
import Grid2 from "@mui/material/Unstable_Grid2";
import { Box, Checkbox, FormControlLabel, IconButton, Popover, Tab, Tabs, Typography } from "@mui/material";
import { FilterList } from "@mui/icons-material";
import { LoadingIndicator } from "../framework/loadingIndicator";
import { IMuiAutocompleteOption, MuiAutocomplete } from "../framework/muiAutocomplete";
import { useAsyncThunkAction } from "../../hooks/framework/useAsyncThunkAction";
import { fetchVehicles } from "../../framework/redux/slices/vehicleSlice";
import { handleApiError } from "../../services/baseService";
import { useAppSelector } from "../../framework/redux/hooks";
import { VehicleListItemDto } from "../../models/vehicle/vehicle";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { StartIconButton } from "../framework/startIconButton";
import { useMediaQuery } from "usehooks-ts";
import { OrderCard } from "./transportOrder/orderCard";

export const TransportOrdersQueue = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(true);
    const isSmallScreen = useMediaQuery("(max-width: 640px)");
    const employeeId = useAppSelector((state) => state.session.id);

    const isWorkQueueInUse = useAppSelector(
        ({ session }) => session.employeeGroupParameters?.isWorkQueueInUse
    );
    const canCreateWorkOrderInMobile = useAppSelector(
        ({ session }) => session.employeeGroupParameters?.canCreateWorkOrderInMobile
    );

    // Order tab names
    const tabOpenOrders = "open";
    const tabOwnOrders = "own";

    const [currentTab, setCurrentTab] = useState<string>(tabOwnOrders);
    const [transportOrders, setTransportOrders] = useState<TransportOrderListItemDto[]>([]);
    const [filteredTransportOrders, setFilteredTransportOrders] = useState<TransportOrderListItemDto[]>([]);
    const [showUpcoming, setShowUpcoming] = useState(false);
    const currentTimeSlot = useSelectCurrentWorkShiftTimeSlot();
    const dateString = Base.dateToDateStr(new Date());

    const [endDate, setEndDate] = useState(Base.dateToDateJSON());
    const statesToFetch = useMemo(
        () => {return [TransportOrderState.OrderCreated, TransportOrderState.PickedUp];},
        []
    );
    const startDate = useMemo(
        () => Base.dateToDateJSON(Base.dateAddMonths(endDate, -3).startOf("month")),
        []
    );

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const id = open ? "order-filters" : undefined;

    useAsyncThunkAction(() => fetchVehicles(), { onError: handleApiError }, []);

    const vehicles = useAppSelector((state) => state.vehicle.idTitles);
    const [selectedVehicles, setSelectedVehicles] = useState<
        IMuiAutocompleteOption[]
    >([]);

    useEffect(() => {
        if (showUpcoming) {
            // Show orders for next two weeks
            setEndDate(Base.dateToDateJSON(Base.dateAddDays(Base.dateToDateJSON(), 14).endOf("week")));
        } else {
            // Back to default time period
            setEndDate(Base.dateToDateJSON());
        }
    }, [showUpcoming]);

    useEffect(() => {
        if (isWorkQueueInUse) {
            setCurrentTab(tabOpenOrders);
        } else {
            setCurrentTab(tabOwnOrders);
        }
    }, [isWorkQueueInUse]);

    const fetchTransportOrders = useCallback(() => {
        if (isWorkQueueInUse) {
            getOpenTransportOrders({
                timeFrameStartDate: startDate,
                timeFrameEndDate: endDate,
            })
                .then((res) => setTransportOrders(res.data))
                .catch((e) => console.error(e))
                .finally(void setLoading(false));
        } else {
            getOwnTransportOrders({
                vehicleId: currentTimeSlot?.vehicleId || "",
                selectedOrderStates: statesToFetch,
                timeFrameStartDate: startDate,
                timeFrameEndDate: endDate,
                orderByAscending: true,
            })
                .then((res) => setTransportOrders(res.data))
                .catch((e) => console.error(e))
                .finally(void setLoading(false));
        }
    }, [currentTimeSlot?.vehicleId, startDate, endDate, isWorkQueueInUse]);

    useEffect(() => {
        setLoading(true);
        fetchTransportOrders();
    }, [fetchTransportOrders]);

    useEffect(() => {
        setFilteredTransportOrders(transportOrders);
        if (transportOrders?.length > 0) {
            if (currentTab === tabOwnOrders) {
                setFilteredTransportOrders(
                    transportOrders.filter(order => order.employeeId === employeeId)
                );
            } else {
                setFilteredTransportOrders(
                    transportOrders.filter(order => order.employeeId === null)
                );
            }
        }
    }, [transportOrders]);

    const handleVehicleChange = (
        selectedVehicles: IMuiAutocompleteOption[]
    ) => {
        setSelectedVehicles(selectedVehicles);
        selectedVehicles.map((vehicle) => {
            const [registerNumber, brand] = vehicle.title.split(", ");
            return {
                id: vehicle.id,
                registerNumber,
                brand,
            } as VehicleListItemDto;
        });
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setCurrentTab(newValue);
        if (newValue === tabOwnOrders) {
            setFilteredTransportOrders(
                transportOrders.filter(order => order.employeeId === employeeId)
            );
        } else {
            setFilteredTransportOrders(
                transportOrders.filter(order => order.employeeId === null)
            );
        }
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <MainLayout
            topComponent={
                <PageTitle
                    header={t("transport.orders.header.queue", { date: dateString })}
                />}
        >
            {!loading && isWorkQueueInUse && (
                <Tabs
                    variant="fullWidth"
                    orientation={isSmallScreen ? "vertical" : "horizontal"}
                    onChange={handleTabChange}
                    value={currentTab}
                    sx={{ marginBottom: 2 }}
                >
                    <Tab label={t("transport.order.openQueue")} value={tabOpenOrders} />
                    <Tab label={t("transport.order.ownOrders")} value={tabOwnOrders} />
                </Tabs>
            )}
            <Grid2 container justifyContent={canCreateWorkOrderInMobile ? "space-between" : "end"} mx={2}>
                {canCreateWorkOrderInMobile && (
                    <Link to={"/transport/order/new"}>
                        <StartIconButton
                            variant="contained"
                            color="success"
                            startIcon={<AddIcon sx={{ mb: "3px" }} />}
                        >
                            {t("transport.order.newOrder")}
                        </StartIconButton>
                    </Link>
                )}
                <IconButton
                    onClick={handleClick}
                    color="primary"
                    size="large"
                >
                    <FilterList fontSize="inherit" />
                </IconButton>
                <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                    }}
                >
                    <Box mx={2} my={1}>
                        <Box>
                            <Grid2 container justifyContent="start" alignItems="center">
                                <FormControlLabel control={
                                    <Checkbox
                                        checked={showUpcoming}
                                        onChange={() => {setShowUpcoming((prev) => !prev);}}
                                    />}
                                    label={t("transport.order.showUpcomingOrders")}
                                />
                            </Grid2>
                        </Box>
                    </Box>
                </Popover>
            </Grid2>

            {vehicles?.length > 0 && (
                <Box mx={2}>
                    <MuiAutocomplete
                        options={vehicles}
                        label={t("common.vehicle")}
                        onChange={handleVehicleChange}
                        value={selectedVehicles}
                    />
                </Box>
            )}

            {loading ? (
                <LoadingIndicator />
            ) : (
                <>
                    {filteredTransportOrders.length > 0 ? (
                        <>
                            {selectedVehicles?.length > 0 ? (
                                filteredTransportOrders.filter(o => selectedVehicles?.some(v => v.id === o.vehicleId))
                                    .map((order) => (
                                        <OrderCard
                                            key={order.id}
                                            transportOrder={order}
                                        />
                                    ))
                            ) : (
                                filteredTransportOrders.map((order) => (
                                    <OrderCard
                                        key={order.id}
                                        transportOrder={order}
                                    />
                                ))
                            )}
                        </>
                    ) : (
                        <Box>
                            <Grid2 container mt={4} gap={2} display="flex" justifyContent="center">
                                <Typography variant="body2" mt={2}>
                                    {t("transport.noOpenOrders")}
                                </Typography>
                            </Grid2>
                        </Box>
                    )}
                </>
            )}
        </MainLayout>
    );
};
